/**
 * Click Location Tracking Component
 *
 * @author Joe Boyle <joboyle@wayfair.com>
 * @copyright 2018 Wayfair LLC - All rights reserved
 */
/* eslint-disable @wayfair/wf-rules/check-prettier */
import React from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';

const DEFAULT_HANDLER = 'onClick';

const TrackClickLocation = props => {
  const {
    tracker: {setClickLocation},
    children,
    clickLocation,
    clickLocationMetadata,
    trackClickHandler,
    destinationUrl,
  } = props;

  const metadata =
    clickLocationMetadata && JSON.stringify(clickLocationMetadata);

  const child = React.Children.only(children);

  if (
    // child is not an HTML element
    typeof child.type !== 'string' &&
    // child does not have a handler prop (onClick by default, can be null)
    !child.props.hasOwnProperty(trackClickHandler)
  ) {
    throw new Error(
      `TrackClickLocation child component is missing '${trackClickHandler}', events will not be sent. Please visit https://docs.csnzoo.com/javascript/patterns/tracking/#click-location-tracking for help.`
    );
  }

  const clickHandlerWithTracking = compose(
    result => {
      setClickLocation(clickLocation, metadata, destinationUrl);
      return result;
    },
    (child.props && child.props[trackClickHandler]) || (() => {})
  );

  return React.cloneElement(child, {
    ...child.props,
    [trackClickHandler]: clickHandlerWithTracking,
  });
};

TrackClickLocation.propTypes = {
  tracker: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
  clickLocation: PropTypes.string.isRequired,
  clickLocationMetadata: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  // trackHandler allows user to map the child's event to `onClick`
  trackClickHandler: PropTypes.string,
  destinationUrl: PropTypes.string,
};

TrackClickLocation.defaultProps = {
  clickLocationMetadata: null,
  trackClickHandler: DEFAULT_HANDLER,
  destinationUrl: null,
};

export default TrackClickLocation;
